// Hotel Setup — Entertainment "Other Places" editor.
// Any local destination (cinema, museum, theatre…) with an official website.
// Images reuse the existing hotel asset-upload system (logo_type place_<id>).
import { useRef, useState } from 'react';
import { AutoTranslateRow } from './AssistantRestaurants';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Loader2, Upload, Trash2, ChevronDown } from 'lucide-react';

const API = (typeof window !== 'undefined' ? window.location.origin : '') || '';
const CATS = ['cinema', 'theatre', 'museum', 'attraction', 'venue', 'festival',
    'tourism', 'convention', 'sports', 'nightlife', 'family', 'other'];

const Tri = ({ obj, base, onChange, placeholder, idSuffix }) => (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {['en', 'fr', 'es'].map((l) => (
            <Input key={l} value={obj[`${base}_${l}`] || ''} placeholder={`${placeholder} (${l.toUpperCase()})`}
                onChange={(e) => onChange(`${base}_${l}`, e.target.value)}
                data-testid={`place-${base}-${l}-${idSuffix}`} className="text-xs" />
        ))}
    </div>
);

export default function AssistantPlaces({ cardId, list, onChange }) {
    const { t } = useTranslation();
    const [openId, setOpenId] = useState(null);
    const [uploading, setUploading] = useState(null);
    const fileRef = useRef(null);
    const uploadIdx = useRef(null);

    const update = (idx, key, value) =>
        onChange(list.map((p, i) => (i === idx ? { ...p, [key]: value } : p)));

    const add = () => {
        const id = `p${Date.now()}`;
        onChange([...list, {
            id, enabled: true, category: 'other',
            name_en: '', name_fr: '', name_es: '',
            description_en: '', description_fr: '', description_es: '',
            url: '', image_url: '', image_size: 140,
        }]);
        setOpenId(id);
    };

    const remove = (idx) => {
        const p = list[idx];
        if (p.image_url) api.delete(`/hotel/admin/delete-logo/${cardId}/place_${p.id}`).catch(() => {});
        onChange(list.filter((_, i) => i !== idx));
    };

    const pickImage = (idx) => {
        uploadIdx.current = idx;
        fileRef.current?.click();
    };

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        const idx = uploadIdx.current;
        if (!file || idx === null || !list[idx]) return;
        if (!file.type.startsWith('image/')) { toast.error(t('assistant.restPhotoType', 'Please select an image file')); return; }
        if (file.size > 5 * 1024 * 1024) { toast.error(t('assistant.restPhotoTooBig', 'Image must be under 5MB')); return; }
        const p = list[idx];
        setUploading(p.id);
        try {
            const fd = new FormData();
            fd.append('logo', file);
            const res = await api.post(`/hotel/admin/upload-logo/${cardId}/place_${p.id}`, fd,
                { headers: { 'Content-Type': 'multipart/form-data' } });
            if (res.data?.logo_url) {
                update(idx, 'image_url', res.data.logo_url);
                toast.success(t('assistant.restPhotoUploaded', 'Photo uploaded — remember to Save'));
            }
        } catch { toast.error(t('assistant.restPhotoFailed', 'Photo upload failed')); }
        finally {
            setUploading(null);
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    const removeImage = (idx) => {
        const p = list[idx];
        api.delete(`/hotel/admin/delete-logo/${cardId}/place_${p.id}`).catch(() => {});
        update(idx, 'image_url', '');
    };

    return (
        <div className="space-y-2" data-testid="assist-places-editor">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile}
                data-testid="place-image-file-input" />
            {list.map((p, i) => (
                <div key={p.id} className="border rounded-lg">
                    <button type="button" onClick={() => setOpenId(openId === p.id ? null : p.id)}
                        className="w-full flex items-center justify-between p-2.5 text-left"
                        data-testid={`place-header-${p.id}`}>
                        <span className="text-xs font-semibold truncate">
                            {p.name_en || t('assistant.placeNew', 'New place')}
                            <span className="text-muted-foreground font-normal"> — {t(`assistant.cat_${p.category}`, p.category)}</span>
                            {p.enabled === false && <span className="text-muted-foreground font-normal"> — {t('assistant.restOff', 'hidden')}</span>}
                        </span>
                        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform shrink-0 ${openId === p.id ? 'rotate-180' : ''}`} />
                    </button>
                    {openId === p.id && (
                        <div className="px-2.5 pb-2.5 space-y-2">
                            <div className="flex items-center justify-between border rounded-lg p-2">
                                <span className="text-xs">{t('assistant.restEnabled', 'Visible to guests')}</span>
                                <Switch checked={p.enabled !== false} onCheckedChange={(v) => update(i, 'enabled', v)}
                                    data-testid={`place-enabled-${p.id}`} />
                            </div>
                            <Tri obj={p} base="name" idSuffix={p.id} placeholder={t('assistant.name', 'Name')} onChange={(k, v) => update(i, k, v)} />
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <select value={p.category || 'other'} onChange={(e) => update(i, 'category', e.target.value)}
                                    className="border rounded-md text-xs p-2 bg-background" data-testid={`place-category-${p.id}`}>
                                    {CATS.map((c) => <option key={c} value={c}>{t(`assistant.cat_${c}`, c)}</option>)}
                                </select>
                                <Input value={p.url || ''} placeholder={t('assistant.placeUrl', 'Official website (https://…)')}
                                    onChange={(e) => update(i, 'url', e.target.value)} className="text-xs" data-testid={`place-url-${p.id}`} />
                            </div>
                            <Tri obj={p} base="description" idSuffix={p.id} placeholder={t('assistant.description', 'Short description')} onChange={(k, v) => update(i, k, v)} />
                            <AutoTranslateRow bases={['name', 'description']} obj={p}
                                apply={(patch) => onChange(list.map((x, j) => (j === i ? { ...x, ...patch } : x)))} idSuffix={p.id} />
                            <div className="border rounded-lg p-2 space-y-2">
                                <p className="text-xs font-medium">{t('assistant.restPhoto', 'Photo / logo')}</p>
                                {p.image_url ? (
                                    <>
                                        <img src={`${API}${p.image_url}`} alt="" style={{ height: `${p.image_size || 140}px` }}
                                            className="w-full object-cover rounded-lg" data-testid={`place-image-preview-${p.id}`} />
                                        <div className="flex items-center gap-2">
                                            <span className="text-[11px] text-muted-foreground shrink-0">{t('assistant.restPhotoSize', 'Display size')}</span>
                                            <input type="range" min={80} max={240} step={10} value={p.image_size || 140}
                                                onChange={(e) => update(i, 'image_size', parseInt(e.target.value, 10))}
                                                className="flex-1" data-testid={`place-image-size-${p.id}`} />
                                            <span className="text-[11px] text-muted-foreground shrink-0">{p.image_size || 140}px</span>
                                        </div>
                                        <div className="flex gap-2">
                                            <Button variant="outline" size="sm" className="text-xs" onClick={() => pickImage(i)}
                                                disabled={uploading === p.id} data-testid={`place-image-replace-${p.id}`}>
                                                {uploading === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                                                {t('assistant.replacePhoto', 'Replace')}
                                            </Button>
                                            <Button variant="ghost" size="sm" className="text-xs text-red-500" onClick={() => removeImage(i)}
                                                data-testid={`place-image-remove-${p.id}`}>
                                                <Trash2 className="w-3.5 h-3.5 mr-1" /> {t('assistant.removePhoto', 'Remove')}
                                            </Button>
                                        </div>
                                    </>
                                ) : (
                                    <Button variant="outline" size="sm" className="text-xs" onClick={() => pickImage(i)}
                                        disabled={uploading === p.id} data-testid={`place-image-upload-${p.id}`}>
                                        {uploading === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                                        {t('assistant.uploadPhoto', 'Upload photo')} <span className="text-muted-foreground ml-1">(PNG/JPG, max 5MB)</span>
                                    </Button>
                                )}
                            </div>
                            <Button variant="ghost" size="sm" className="text-xs text-red-500 w-full" onClick={() => remove(i)}
                                data-testid={`place-delete-${p.id}`}>
                                <Trash2 className="w-3.5 h-3.5 mr-1" /> {t('assistant.deletePlace', 'Delete this place')}
                            </Button>
                        </div>
                    )}
                </div>
            ))}
            <Button variant="outline" size="sm" className="text-xs" onClick={add} data-testid="place-add-btn">
                + {t('assistant.addPlace', 'Add Other Place')}
            </Button>
        </div>
    );
}
